"use client";

/**
 * The header message board (Gordon 2026-10-30) — the header's background as a
 * full-height ticker. One message glides across every few seconds (6 s by
 * default): a chat line, then a new release with its cover, then music tech
 * news, BB's own messages, events and marketplace goods / services / talent.
 * Text, GIFs and film clips; every item is a link (a chat opens that chat).
 *
 * Visitors: Super Admin decides what plays. Their chat lane is REAL: the
 * newest messages in the public rooms Super Admin picked, and open collab
 * posts — never sample text (Gordon 2026-11-01). Clicking one goes through
 * sign-in into that room (joined as a member) or that collab post, ready to
 * answer. Signed-in users: their unread chats and their own choice of
 * sources, fonts and colours (per kind, or per person).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Calendar, Disc3, Megaphone, MessageSquare, Newspaper, ShoppingBag } from "lucide-react";
import {
  DEFAULT_HEADER_BOARD, loadBoardFeed, loadVisitorBoard, subscribeHeaderBoardSettings,
  type BoardFeed, type BoardItem, type HeaderBoardSettings, type VisitorBoardFeed,
} from "@/services/header-board";
import {
  BOARD_FONTS, DEFAULT_BOARD_STYLES,
  type BoardItemKind, type BoardStyle, type UserHeaderBoardPrefs,
} from "@/services/user-layout-prefs";
import { cn } from "@/lib/utils";
import { optimizedImage } from '@/lib/optimized-image';

const KIND_ICON: Record<BoardItemKind, typeof MessageSquare> = {
  chat: MessageSquare, release: Disc3, news: Newspaper, bb: Megaphone, event: Calendar, product: ShoppingBag,
};
/** The order the board walks through (Gordon: chat → release → tech news …). */
const SEQUENCE: BoardItemKind[] = ["chat", "release", "news", "bb", "event", "product"];

interface Props {
  /** Signed-in user's id; undefined = visitor */
  uid?: string;
  prefs?: UserHeaderBoardPrefs;
  /** The user's unread chats, newest first (built by the app shell) */
  chats?: BoardItem[];
  /** The favorites strip is open: one message at a time — the strip steps
   *  aside while it passes and comes back after (`bb-board-busy` event). */
  sharing: boolean;
}

export function HeaderBoard({ uid, prefs, chats = [] }: Props) {
  const t = useTranslations("headerBoard");
  const [settings, setSettings] = useState<HeaderBoardSettings>(DEFAULT_HEADER_BOARD);
  const [feed, setFeed] = useState<BoardFeed>({ releases: [], news: [], events: [], products: [] });
  const [reduceMotion, setReduceMotion] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => subscribeHeaderBoardSettings(setSettings), []);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const on = () => setReduceMotion(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  const visitor = !uid;
  const newsTopics = (visitor ? settings.newsTopics : prefs?.newsTopics) ?? settings.newsTopics;
  const sources = visitor
    ? { bb: true, ...settings.visitor }
    : (prefs?.sources ?? { bb: true, chat: true, releases: true, news: true, events: true, products: true });

  // Feed: once, then every 10 minutes.
  useEffect(() => {
    let alive = true;
    const load = () => loadBoardFeed({ uid, releases: sources.releases, news: sources.news, newsTopics, events: sources.events, products: sources.products })
      .then(f => { if (alive) setFeed(f); })
      .catch(() => {});
    void load();
    const timer = setInterval(load, 10 * 60_000);
    return () => { alive = false; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, sources.releases, sources.news, sources.events, sources.products, newsTopics.tech, newsTopics.business, newsTopics.bb]);

  // Visitors: real public-room messages + open collab posts, every 5 minutes.
  const [visitorFeed, setVisitorFeed] = useState<VisitorBoardFeed>({ chats: [], collabs: [] });
  useEffect(() => {
    if (!visitor || !sources.chat) return;
    let alive = true;
    const load = () => loadVisitorBoard().then(f => { if (alive) setVisitorFeed(f); }).catch(() => {});
    void load();
    const timer = setInterval(load, 5 * 60_000);
    return () => { alive = false; clearInterval(timer); };
  }, [visitor, sources.chat]);

  // Every lane of the sequence, in order.
  const lanes = useMemo<Record<BoardItemKind, BoardItem[]>>(() => {
    // A visitor's click signs in first, then lands in the room (joined) or on
    // the post — answering needs an account.
    const signInTo = (path: string) => `/signin?redirect=${encodeURIComponent(path)}`;
    const visitorChats: BoardItem[] = visitorFeed.chats.map(m => ({
      id: `room-${m.id}`, kind: "chat", text: m.text,
      sub: m.roomName ? `${m.senderName} · ${m.roomName}` : m.senderName,
      href: signInTo(`/home?room=${m.roomId}`),
    }));
    const visitorCollabs: BoardItem[] = visitorFeed.collabs.map(p => ({
      id: `collab-${p.id}`, kind: "chat", text: p.title,
      sub: p.roles.length ? t("collabSubRoles", { owner: p.ownerName, roles: p.roles.join(", ") }) : t("collabSub", { owner: p.ownerName }),
      href: signInTo(`/collab?post=${p.id}`),
    }));
    // Chat and collab lines take turns.
    const mixed: BoardItem[] = [];
    for (let i = 0; i < Math.max(visitorChats.length, visitorCollabs.length); i++) {
      if (visitorChats[i]) mixed.push(visitorChats[i]);
      if (visitorCollabs[i]) mixed.push(visitorCollabs[i]);
    }
    const chatLane: BoardItem[] = visitor ? mixed : chats;
    return {
      chat: sources.chat ? chatLane : [],
      release: sources.releases ? feed.releases : [],
      news: sources.news ? feed.news : [],
      bb: sources.bb ? settings.messages.map(m => ({ ...m, id: `bb-${m.id}`, kind: "bb" as const })) : [],
      event: sources.events ? feed.events : [],
      product: sources.products ? feed.products : [],
    };
  }, [settings, feed, chats, visitor, visitorFeed, sources.chat, sources.releases, sources.news, sources.bb, sources.events, sources.products, t]);

  // The sequencer: every gapSec start the next item of the next non-empty lane.
  const [active, setActive] = useState<{ key: number; item: BoardItem }[]>([]);
  const cursor = useRef({ lane: 0, index: {} as Partial<Record<BoardItemKind, number>> });
  const keyRef = useRef(0);
  const nextItem = useCallback((): BoardItem | null => {
    for (let tries = 0; tries < SEQUENCE.length; tries++) {
      const kind = SEQUENCE[cursor.current.lane % SEQUENCE.length];
      cursor.current.lane++;
      const lane = lanes[kind];
      if (!lane.length) continue;
      const i = (cursor.current.index[kind] ?? 0) % lane.length;
      cursor.current.index[kind] = i + 1;
      return lane[i];
    }
    return null;
  }, [lanes]);

  // ONE message on screen at a time, then a quiet gap (default 9 s, never
  // under 6) before the next starts (Gordon 2026-10-30: "overlapping and
  // doubling … 9 seconds between them"). Refs, not effects, decide when to
  // launch, so a settings/feed refresh can never start a second message.
  const gapMs = Math.max(6, settings.gapSec || 18) * 1000;
  const activeRef = useRef<{ key: number; item: BoardItem }[]>([]);
  const waitingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tick, setTick] = useState(0);
  const tryLaunch = useCallback(() => {
    if (activeRef.current.length > 0 || waitingRef.current) return;
    const item = nextItem();
    if (!item) {
      // Feed still loading — look again shortly.
      waitingRef.current = true;
      timerRef.current = setTimeout(() => { waitingRef.current = false; setTick(n => n + 1); }, 2000);
      return;
    }
    const next = [{ key: ++keyRef.current, item }];
    activeRef.current = next;
    setActive(next);
  }, [nextItem]);
  useEffect(() => {
    if (settings.enabled) tryLaunch();
  }, [settings.enabled, tryLaunch, tick]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const finished = useCallback((key: number) => {
    if (!activeRef.current.some(a => a.key === key)) return;
    activeRef.current = [];
    setActive([]);
    waitingRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    // Reduced motion: messages swap in place, so a long empty gap read as a
    // frozen board — keep it short.
    timerRef.current = setTimeout(() => { waitingRef.current = false; setTick(n => n + 1); }, reduceMotion ? 2000 : gapMs);
  }, [gapMs, reduceMotion]);

  useEffect(() => {
    // Reduced motion: each message fades in, reads for 7 s, then makes way.
    if (!reduceMotion || active.length === 0) return;
    const timer = setTimeout(() => finished(active[0].key), 7000);
    return () => clearTimeout(timer);
  }, [reduceMotion, active, finished]);

  // Let the header know a message is passing (the favorites strip steps aside).
  const busy = active.length > 0;
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("bb-board-busy", { detail: { busy } }));
  }, [busy]);
  useEffect(() => () => { window.dispatchEvent(new CustomEvent("bb-board-busy", { detail: { busy: false } })); }, []);

  const styleFor = (item: BoardItem): BoardStyle => {
    // Default for everyone: one condensed sans in the theme's colours
    // (Gordon 2026-10-31). Only a signed-in user's own choices change it.
    if (visitor) return DEFAULT_BOARD_STYLES[item.kind];
    const person = item.personId ? prefs?.people?.[item.personId] : undefined;
    if (person) return { font: person.font, color: person.color };
    return prefs?.styles?.[item.kind] ?? DEFAULT_BOARD_STYLES[item.kind];
  };

  if (!settings.enabled) return null;
  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden pointer-events-none [mask-image:linear-gradient(90deg,transparent,#000_6%,#000_94%,transparent)]"
      aria-live="off"
    >
      {active.map(({ key, item }) => (
        <GlideItem
          key={key}
          item={item}
          style={styleFor(item)}
          speed={settings.speed || 90}
          still={reduceMotion}
          interactive
          containerRef={containerRef}
          onDone={() => finished(key)}
        />
      ))}
    </div>
  );
}

function GlideItem({ item, style, speed, still, interactive, containerRef, onDone }: {
  item: BoardItem; style: BoardStyle; speed: number; still: boolean; interactive: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>; onDone: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const anim = useRef<Animation | null>(null);

  useEffect(() => {
    const el = ref.current, box = containerRef.current;
    if (!el || !box || still) return;
    const from = box.clientWidth, to = -el.offsetWidth, v = Math.max(20, speed);
    const duration = ((from - to) / v) * 1000;
    let done = false;
    const finish = () => { if (!done) { done = true; onDone(); } };
    // Some WebKit builds (an iPhone 12 in Chrome, Gordon 2026-10-31) left the
    // message standing still. Web Animations first; a CSS transition if that
    // API is missing or throws — and a watchdog either way, so the board
    // always moves on even when `finish` never arrives.
    let usedTransition = false;
    try {
      if (typeof el.animate !== "function") throw new Error("no WAAPI");
      anim.current = el.animate(
        [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
        { duration, easing: "linear", fill: "forwards" },
      );
      anim.current.onfinish = finish;
    } catch {
      usedTransition = true;
      el.style.transform = `translateX(${from}px)`;
      void el.offsetWidth; // commit the start position before moving
      el.style.transition = `transform ${duration}ms linear`;
      el.style.transform = `translateX(${to}px)`;
      el.addEventListener("transitionend", finish, { once: true });
    }
    let last = -1, stalls = 0;
    const watchdog = setInterval(() => {
      const a = anim.current;
      if (usedTransition || !a) return;
      if (a.playState === "finished") return finish();
      if (a.playState === "paused") { stalls = 0; return; } // a mouse is reading it
      const now = Number(a.currentTime ?? 0);
      stalls = now === last ? stalls + 1 : 0;
      last = now;
      if (stalls >= 4) finish(); // stuck for 4 s — let the next message come
    }, 1000);
    const hardStop = setTimeout(finish, duration + 60_000); // never longer than this
    // An open favorites strip moves with it: pushed out ahead, back right behind.
    window.dispatchEvent(new CustomEvent("bb-board-glide", {
      detail: { boardLeft: box.getBoundingClientRect().left, from, width: el.offsetWidth, speed: v },
    }));
    return () => { clearInterval(watchdog); clearTimeout(hardStop); anim.current?.cancel(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Icon = KIND_ICON[item.kind];
  const body = (
    <>
      {item.videoUrl ? (
        <video src={item.videoUrl} muted loop autoPlay playsInline className="h-[calc(var(--header-height)_-_8px)] aspect-square shrink-0 rounded-md object-cover" />
      ) : item.imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={optimizedImage(item.imageUrl, 96)} alt="" className="h-[calc(var(--header-height)_-_8px)] aspect-square shrink-0 rounded-md object-cover" />
      ) : (
        <Icon className={cn("h-6 w-6 shrink-0", !style.color && "text-primary")} style={style.color ? { color: style.color } : undefined} aria-hidden />
      )}
      <span
        // No background behind messages — a strong text shadow keeps them readable on anything.
        className={cn(
          "whitespace-nowrap leading-none",
          // Custom colours keep a dark shadow; theme-coloured text gets
          // a halo in the theme's background colour so it reads in light themes too.
          style.color
            ? "bb-board-custom-text-shadow"
            : "text-foreground bb-board-theme-text-shadow",
        )}
        style={{ fontFamily: BOARD_FONTS[style.font], ...(style.color ? { color: style.color } : {}), fontSize: "calc(var(--header-height) * 0.42)", textTransform: style.font === "impact" ? "uppercase" : undefined }}
      >
        {item.sub ? <span className="opacity-80">{item.sub}: </span> : null}{item.text}
      </span>
    </>
  );
  // Empty space before and after every message — a thumbnail and a half, so
  // it still reads as a full thumbnail after the edge fade (Gordon 2026-10-30).
  const cls = cn(
    "absolute inset-y-0 left-0 flex items-center gap-3 px-[calc(var(--header-height)_*_1.5)]",
    still && "left-1/2 -translate-x-1/2 animate-in fade-in duration-700",
    interactive ? "pointer-events-auto cursor-pointer" : "pointer-events-none",
  );
  // Hover-to-read is for a MOUSE only. iOS turns a tap into mouseenter and
  // never sends mouseleave, so a tapped message froze in the header for good
  // (Gordon 2026-10-31: "iPhone the header messages are not scrolling by, stuck").
  const pause = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    anim.current?.pause();
    window.dispatchEvent(new CustomEvent("bb-board-glide-pause", { detail: { paused: true } }));
  };
  const play = () => {
    if (anim.current?.playState !== "paused") return;
    anim.current.play();
    window.dispatchEvent(new CustomEvent("bb-board-glide-pause", { detail: { paused: false } }));
  };

  if (item.event) {
    const { name, detail } = item.event;
    return (
      <div ref={ref} role="button" tabIndex={interactive ? 0 : -1} className={cls} onPointerEnter={pause} onPointerLeave={play}
        onClick={() => window.dispatchEvent(new CustomEvent(name, { detail }))}
        onKeyDown={e => { if (e.key === "Enter") window.dispatchEvent(new CustomEvent(name, { detail })); }}>
        {body}
      </div>
    );
  }
  return (
    <div ref={ref} className={cls} onPointerEnter={pause} onPointerLeave={play}>
      {item.href ? <Link href={item.href} className="flex h-full items-center gap-3" tabIndex={interactive ? 0 : -1}>{body}</Link> : body}
    </div>
  );
}
